import type { SEOIntent } from "@/lib/seo-intents";

export const REPORT_INTENT_SLUGS = [
  "career-report",
  "marriage-report",
  "dosha-report",
  "sade-sati-report",
  "pitra-dosh-report",
  "mahadasha-forecast",
  "career-astrology-report",
  "muhurat-report",
  "dosha-remedy-plan",
  "annual-forecast",
  "numerology-report",
  "gemstone-report",
  "baby-name-report",
  "lal-kitab-report",
  "gochar-report",
] as const;

export type ReportIntentSlug = (typeof REPORT_INTENT_SLUGS)[number];

export interface ReportIntentConfig {
  slug: ReportIntentSlug;
  reportSlug: string;
  intent: SEOIntent;
  eyebrow: string;
  title: string;
  description: string;
  lead: string;
  checkpoints: string[];
  fit: string;
  inputSurface: "kundali" | "matching";
  offerHeadline: string;
  offerSubhead: string;
  collectInputsLabel: string;
  customerProblem: string;
  faq: { q: string; a: string }[];
}

export const REPORT_INTENT_PAGES: Record<ReportIntentSlug, ReportIntentConfig> = {
  "career-report": {
    slug: "career-report",
    reportSlug: "career-report",
    intent: "chart_report",
    eyebrow: "Career Report",
    title: "Why does your career keep stalling despite hard work?",
    description:
      "A personal Vedic career report for 10th house, D10/Dasamsa, Shani, Jupiter, dasha timing, wealth yogas, and career upay.",
    lead: "Career does not move only because effort increases. Your 10th house, D10, Shani, Jupiter, Sun, Budh, and active dasha decide when work becomes visible and when patience is smarter.",
    checkpoints: [
      "10th house and D10/Dasamsa career direction",
      "Current dasha and transit support for job or business",
      "Wealth yogas and blocked income patterns",
      "Career upay matched to the graha causing delay",
    ],
    fit: "Best when promotions, job changes, business timing, or income growth feel delayed despite consistent effort.",
    inputSurface: "kundali",
    offerHeadline: "Get your Career Report",
    offerSubhead:
      "Generate your kundali first, then get the 10th house, D10, dasha timing, wealth yogas, and career upay report matched to your chart.",
    collectInputsLabel: "Enter birth details for Career Report",
    customerProblem: "career",
    faq: [
      {
        q: "Will it tell me the right profession?",
        a: "It shows the work environments, strengths, timing, and career pressure points your chart supports instead of giving a generic job list.",
      },
      {
        q: "Does it include business guidance?",
        a: "Yes. It reads career and finance through the 10th, 11th, dasha, and relevant graha strength.",
      },
    ],
  },
  "marriage-report": {
    slug: "marriage-report",
    reportSlug: "marriage-report",
    intent: "chart_report",
    eyebrow: "Marriage Report",
    title: "Why does the same relationship doubt return before marriage?",
    description:
      "A family-shareable Marriage Report for Kundali Milan, 7th house, Mangal Dosh, D9/Navamsha, dasha timing, and practical upay.",
    lead: "Marriage matching should not stop at a score. The 7th house, Mangal placement, Nadi/Bhakoot, D9/Navamsha, and both partners' dasha timing decide whether the match is stable, delayed, or needs upay.",
    checkpoints: [
      "Full 36-point Gun Milan with the weak kootas explained",
      "Mangal Dosh, Nadi, Bhakoot, Gana, and cancellation checks",
      "D9/Navamsha and dasha timing for marriage stability",
      "Clear strengths, risks, remedies, and family-shareable summary",
    ],
    fit: "Best before families decide, when the match looks good but one chart point or repeated doubt needs a proper Vedic cross-check.",
    inputSurface: "matching",
    offerHeadline: "Get your Marriage Report",
    offerSubhead:
      "Enter both birth details first. The report uses the actual Kundali Milan result, Mangal Dosh, D9/Navamsha, and dasha timing before checkout.",
    collectInputsLabel: "Enter both birth details",
    customerProblem: "marriage",
    faq: [
      {
        q: "Why do you need both birth details?",
        a: "The paid Marriage Report is based on two charts. Without both birth details, Gun Milan, Mangal Dosh cross-check, D9/Navamsha, and dasha synchrony would be incomplete.",
      },
      {
        q: "Is this more than a Gun Milan score?",
        a: "Yes. The score is only the first layer. The report explains the weak areas, cancellations, dasha timing, relationship risks, and upay families can actually discuss.",
      },
    ],
  },
  "dosha-report": {
    slug: "dosha-report",
    reportSlug: "dosha-remedy-plan",
    intent: "remedy_dosha",
    eyebrow: "Dosha Report",
    title: "Why follow a dosha upay before checking your chart?",
    description:
      "A chart-specific Dosha Report for Mangal, Kaal Sarp, Pitra, Shani and other pressure checks, with severity, cancellation, and safe upay.",
    lead: "A dosha report should reduce fear, not create it. The right read checks whether a dosha actually fires, whether cancellation applies, which life area is affected, and which upay is safe before any strong remedy is suggested.",
    checkpoints: [
      "Mangal, Kaal Sarp, Pitra, Shani and other chart pressure checks",
      "Severity and cancellation before remedies",
      "Affected life areas separated clearly",
      "Safe upay routine: mantra, donation, vrata, shanti, or product guidance",
    ],
    fit: "Best when one or more doshas have been mentioned and you need the safe next step instead of a generic remedy list.",
    inputSurface: "kundali",
    offerHeadline: "Get your Dosha Report",
    offerSubhead:
      "Generate your kundali first, then get the dosha severity, cancellation checks, active life area, and safe upay sequence matched to your chart.",
    collectInputsLabel: "Enter birth details for Dosha Report",
    customerProblem: "dosha",
    faq: [
      {
        q: "Will it check multiple doshas?",
        a: "Yes. It focuses on the doshas available in the Ishvaram engine and explains which one is actually active for your chart.",
      },
      {
        q: "Will it recommend a strong ritual immediately?",
        a: "No. The report checks cancellation and severity first, then recommends the lighter or stronger upay your chart actually supports.",
      },
    ],
  },
  "sade-sati-report": {
    slug: "sade-sati-report",
    reportSlug: "sade-sati",
    intent: "remedy_dosha",
    eyebrow: "Sade Sati Report",
    title: "Is Shani testing your Moon sign, or is another dasha creating the pressure?",
    description:
      "A personal Sade Sati report for Shani phase, natal Saturn, dasha context, pressure areas, and chart-specific upay.",
    lead: "Sade Sati should not be read from Moon sign alone. Your natal Shani, active dasha, and house pressure decide whether this phase feels like delay, discipline, family pressure, career restructuring, or inner heaviness.",
    checkpoints: [
      "Current Sade Sati phase from your Moon sign",
      "Natal Shani placement and strength",
      "Dasha context so Shani is not blamed for everything",
      "Simple Shani upay matched to your chart",
    ],
    fit: "Best when you already know or suspect Sade Sati and want a chart-level answer before doing heavy Shani remedies.",
    inputSurface: "kundali",
    offerHeadline: "Get your Sade Sati Report",
    offerSubhead:
      "Generate your kundali first, then get Shani phase, natal Saturn, dasha context, pressure areas, and chart-specific upay.",
    collectInputsLabel: "Enter birth details for Sade Sati Report",
    customerProblem: "sade_sati",
    faq: [
      {
        q: "Will this tell me exact Sade Sati dates?",
        a: "It gives the phase context from your Moon sign and connects it with your kundali. Exact timing is computed after you submit birth details.",
      },
      {
        q: "Is it only for people currently in Sade Sati?",
        a: "No. It can also help if Sade Sati recently ended or is approaching, because the report reads Shani's natal promise and dasha context.",
      },
    ],
  },
  "pitra-dosh-report": {
    slug: "pitra-dosh-report",
    reportSlug: "pitra-dosh",
    intent: "remedy_dosha",
    eyebrow: "Pitra Dosh Report",
    title: "Is Pitra Dosh really present, or is your chart showing a different family pattern?",
    description:
      "A personal Pitra Dosh report for Sun, 9th house, Rahu/Ketu, cancellation checks, dasha activation, and safe ancestral upay.",
    lead: "Pitra Dosh is serious enough that it should be confirmed before any tarpan, shradh, donation, or puja sequence is started. The report checks the rule, severity, cancellation, and activation window first.",
    checkpoints: [
      "Sun, 9th house, 9th lord, Rahu/Ketu and Saturn checks",
      "Cancellation factors before severity is finalised",
      "Family, father-line, career, and money pattern interpretation",
      "Tarpan, shradh, mantra, donation, or lighter upay sequence",
    ],
    fit: "Best when recurring family or career blocks are being explained as ancestral karma and you want the chart verified first.",
    inputSurface: "kundali",
    offerHeadline: "Get your Pitra Dosh Report",
    offerSubhead:
      "Generate your kundali first, then get Sun, 9th house, Rahu/Ketu, cancellation checks, dasha activation, and safe ancestral upay.",
    collectInputsLabel: "Enter birth details for Pitra Dosh Report",
    customerProblem: "pitra_dosh",
    faq: [
      {
        q: "Is this different from the Pitra Dosh calculator?",
        a: "Yes. The calculator gives the quick signal. The report explains severity, cancellation, activation, affected life areas, and the upay sequence.",
      },
      {
        q: "Will it recommend an expensive ritual?",
        a: "Only if the chart supports a stronger upay. Mild or cancelled patterns are handled with lighter, safer upay.",
      },
    ],
  },
  "mahadasha-forecast": {
    slug: "mahadasha-forecast",
    reportSlug: "mahadasha-forecast",
    intent: "chart_report",
    eyebrow: "Mahadasha Forecast",
    title: "Which mahadasha is setting the timing behind your next 12 months?",
    description:
      "A 12-month Mahadasha Forecast with current mahadasha, antardasha, transition timing, decision windows, and graha-specific upay.",
    lead: "Mahadasha is the background climate of your life. The same effort can feel blocked or supported depending on the active graha and sub-period. This report turns that timing into practical next steps.",
    checkpoints: [
      "Current mahadasha and antardasha from Moon nakshatra",
      "Next transition points and month-level timing",
      "Career, money, marriage, family, health, and spiritual focus",
      "Graha-specific upay for the active period",
    ],
    fit: "Best when you are deciding whether to act, wait, change job, start something, or understand why a phase has shifted.",
    inputSurface: "kundali",
    offerHeadline: "Get your Mahadasha Forecast",
    offerSubhead:
      "Generate your kundali first, then get current mahadasha, antardasha, transition timing, decision windows, and graha-specific upay.",
    collectInputsLabel: "Enter birth details for Mahadasha Forecast",
    customerProblem: "mahadasha",
    faq: [
      {
        q: "Is this the same as Varshphal?",
        a: "No. Varshphal reads the year chart. This report reads your active dasha period and the next 12 months of sub-period movement.",
      },
      {
        q: "Can I use it without exact birth time?",
        a: "Use the most accurate time available. The dasha comes from Moon nakshatra, while exact birth time improves the house context.",
      },
    ],
  },
  "career-astrology-report": {
    slug: "career-astrology-report",
    reportSlug: "career-report",
    intent: "chart_report",
    eyebrow: "Career Astrology Report",
    title: "Why does your hard work not convert into career results at the right time?",
    description:
      "A Vedic career astrology report for 10th house, D10/Dasamsa, Shani, Jupiter, dasha timing, wealth yogas, and career upay.",
    lead: "Career timing is not only about effort. The 10th house, D10, Shani, Jupiter, Sun, Mercury, and the active dasha decide when work becomes visible and when patience is smarter.",
    checkpoints: [
      "10th house and Dasamsa career direction",
      "Current dasha and transit support for job or business",
      "Wealth yogas and blocked income patterns",
      "Career upay matched to the graha causing delay",
    ],
    fit: "Best when promotions, job changes, business timing, or income growth feel delayed despite consistent effort.",
    inputSurface: "kundali",
    offerHeadline: "Get your Career Report",
    offerSubhead:
      "Generate your kundali first, then get the 10th house, D10, dasha timing, wealth yogas, and career upay report matched to your chart.",
    collectInputsLabel: "Enter birth details for Career Report",
    customerProblem: "career",
    faq: [
      {
        q: "Will it tell me the right profession?",
        a: "It shows the work environments, strengths, and timing your chart supports instead of giving a generic job list.",
      },
      {
        q: "Does it include business guidance?",
        a: "Yes. It reads career and finance through the 10th, 11th, dasha, and relevant graha strength.",
      },
    ],
  },
  "muhurat-report": {
    slug: "muhurat-report",
    reportSlug: "muhurat-report",
    intent: "timing_muhurat",
    eyebrow: "Personal Muhurat Report",
    title: "Why does a shubh time need your city, purpose, and kundali together?",
    description:
      "A personal Muhurat report for purpose-specific timing, avoid windows, panchang, sankalp, and ritual checklist.",
    lead: "A public choghadiya slot is only the outer layer. A cleaner muhurat checks city panchang, purpose, Moon sign, dasha pressure, avoid windows, and the sankalp you should take before starting.",
    checkpoints: [
      "Purpose-specific timing instead of a generic table",
      "Tithi, nakshatra, yoga, karana, weekday, and choghadiya",
      "Rahu Kaal, Yamagandam, Gulika Kaal, and personal dasha pressure",
      "Sankalp wording and practical ritual checklist",
    ],
    fit: "Best before launches, purchases, griha pravesh, travel, naming, rituals, or family decisions where timing matters.",
    inputSurface: "kundali",
    offerHeadline: "Get your Muhurat Report",
    offerSubhead:
      "Generate your kundali first, then get purpose-specific timing, avoid windows, panchang, sankalp, and ritual checklist.",
    collectInputsLabel: "Enter details for Muhurat Report",
    customerProblem: "muhurat",
    faq: [
      {
        q: "Is this better than a choghadiya calculator?",
        a: "It uses the public timing layer but adds your purpose, city, birth chart, avoid windows, and sankalp.",
      },
      {
        q: "Can it replace a wedding muhurat?",
        a: "Use it for initial timing guidance. A final wedding muhurat still benefits from family constraints and pandit confirmation.",
      },
    ],
  },
  "annual-forecast": {
    slug: "annual-forecast",
    reportSlug: "annual-forecast",
    intent: "chart_report",
    eyebrow: "Annual Forecast 2026-27",
    title: "Why will some months in the year ahead lift you and others test you?",
    description:
      "A personal Annual Forecast (varshphal) for the year ahead - month-by-month dasha and transit timing, the windows that favour you, and the ones to plan around.",
    lead: "A generic yearly rashifal reads the same for millions. Your varshphal is built from your own chart - the running mahadasha and antardasha, this year's Shani and Jupiter transits over your houses, and the specific months they turn supportive or heavy.",
    checkpoints: [
      "Month-by-month timing for the next 12 months, not a generic yearly blurb",
      "Your running mahadasha and antardasha and what each is ripening",
      "Shani (Saturn) and Jupiter transits over your houses, with dates",
      "The strong windows to act and the heavy windows to plan around",
    ],
    fit: "Best when you want to time the year ahead - career moves, money decisions, marriage, travel, or major commitments - to your own chart.",
    inputSurface: "kundali",
    offerHeadline: "Get your Annual Forecast",
    offerSubhead:
      "Generate your kundali first, then get your month-by-month dasha and transit timing for the year ahead.",
    collectInputsLabel: "Enter details for Annual Forecast",
    customerProblem: "forecast",
    faq: [
      {
        q: "How is this different from a free yearly rashifal?",
        a: "A yearly rashifal is written for a whole rashi. This is computed from your exact birth chart - your dasha, your transits, your houses - so the timing is yours, not a sign's.",
      },
      {
        q: "Which year does it cover?",
        a: "The 12 months ahead from your reading, mapped to your running dasha and the live Shani and Jupiter transits.",
      },
    ],
  },
  "dosha-remedy-plan": {
    slug: "dosha-remedy-plan",
    reportSlug: "dosha-remedy-plan",
    intent: "remedy_dosha",
    eyebrow: "Dosha Remedy Plan",
    title: "Why choose an upay before confirming the dosha and cancellation?",
    description:
      "A chart-specific Dosha Remedy Plan for severity, cancellation, affected life area, safe upay, mantra routine, and matched remedy guidance.",
    lead: "A dosha report should reduce fear, not create it. This plan checks whether a dosha actually fires, whether cancellation applies, and which upay is safe before any strong remedy is suggested.",
    checkpoints: [
      "Mangal, Kaal Sarp, Pitra, Shani and other chart pressure checks",
      "Severity and cancellation before remedies",
      "Affected life areas separated clearly",
      "Safe upay routine: mantra, donation, vrata, shanti, or product guidance",
    ],
    fit: "Best when one or more doshas have been mentioned and you need the safe next step instead of a generic remedy list.",
    inputSurface: "kundali",
    offerHeadline: "Get your Dosha Remedy Plan",
    offerSubhead:
      "Generate your kundali first, then get dosha severity, cancellation checks, affected life area, and safe upay guidance.",
    collectInputsLabel: "Enter birth details for Dosha Plan",
    customerProblem: "dosha",
    faq: [
      {
        q: "Will it check multiple doshas?",
        a: "Yes. It focuses on the doshas available in the Ishvaram engine and explains which one is actually active for your chart.",
      },
      {
        q: "Does it recommend products?",
        a: "Only where the chart supports a product upay. The report prioritises safe routine and classical remedy order first.",
      },
    ],
  },
  "numerology-report": {
    slug: "numerology-report",
    reportSlug: "numerology-life",
    intent: "chart_report",
    eyebrow: "Numerology Life Report",
    title: "Why do your numbers keep pulling you toward the same delays?",
    description:
      "A personal Ank Jyotish report for your mulank and bhagyank, the graha each rules, name-number harmony, lucky numbers and days, and the year ahead.",
    lead: "Your mulank and bhagyank are not lucky-charm trivia — they are ruled by the same grahas sitting in your kundali. When your name-number fights your birth number, the friction shows up as repeated delay. This report names the graha behind each number and the upay that settles it.",
    checkpoints: [
      "Mulank and bhagyank with the graha each one rules",
      "Name-number harmony — where your name works with your birth number and where it fights it",
      "Lucky numbers, days, and directions matched to your ruling grahas",
      "Year-ahead numerology cycle and numerology upay",
    ],
    fit: "Best when the same money, career, or relationship pattern keeps repeating and you want the number-and-graha reason before a naming or business decision.",
    inputSurface: "kundali",
    offerHeadline: "Get your Numerology Life Report",
    offerSubhead:
      "Generate your kundali first, then get your mulank, bhagyank, name-number harmony, lucky numbers, and the year-ahead cycle read against your grahas.",
    collectInputsLabel: "Enter birth details for Numerology Report",
    customerProblem: "numerology",
    faq: [
      {
        q: "Is this only about lucky numbers?",
        a: "Lucky numbers are one part. The report reads the graha each of your numbers rules, checks whether your name-number supports your birth number, and gives the upay for the pattern your chart carries.",
      },
      {
        q: "Do I need my birth time?",
        a: "Numerology runs from your date of birth and name, so those are enough for the core report. Birth time only sharpens the kundali cross-read of your ruling grahas.",
      },
    ],
  },
  "gemstone-report": {
    slug: "gemstone-report",
    reportSlug: "gemstone-recommendation",
    intent: "remedy_dosha",
    eyebrow: "Rudraksha & Ratna Report",
    title: "Why did the gemstone you were told to wear change nothing?",
    description:
      "A personal ratna and rudraksha report: which stone your chart needs by lagna and dasha, the wearable rudraksha equivalent, and the full dhaaran vidhi.",
    lead: "A ratna strengthens a graha, so the wrong stone strengthens the wrong planet — which is why a rashi-based gemstone often does nothing or makes things heavier. This report reads your lagna, functional benefics, and active dasha first, then names the stone your chart actually needs.",
    checkpoints: [
      "The ratna your chart needs, ranked by lagna lord and active dasha",
      "Stones to avoid — the grahas your chart does not want strengthened",
      "The wearable rudraksha (mukhi) equivalent when a ratna is not suitable",
      "Full dhaaran vidhi: metal, finger, weight, day, and graha mantra",
    ],
    fit: "Best before you buy a gemstone, or when a stone you already wear feels wrong and you want the lagna-and-dasha reason first.",
    inputSurface: "kundali",
    offerHeadline: "Get your Rudraksha & Ratna Report",
    offerSubhead:
      "Generate your kundali first, then get the ratna ranked to your lagna and dasha, the rudraksha equivalent, and the dhaaran vidhi to activate it.",
    collectInputsLabel: "Enter birth details for Ratna Report",
    customerProblem: "gemstone",
    faq: [
      {
        q: "Can't I just wear the stone for my rashi?",
        a: "Rashi-based charts ignore your lagna and dasha, which is why they often strengthen a graha your chart does not want strong. This report reads your functional benefics first, so the stone is matched to your chart.",
      },
      {
        q: "What if I cannot afford the recommended ratna?",
        a: "The report gives a wearable rudraksha equivalent for each ratna, so the same graha has an accessible remedy without a large spend.",
      },
    ],
  },
  "baby-name-report": {
    slug: "baby-name-report",
    reportSlug: "baby-name",
    intent: "chart_report",
    eyebrow: "Baby Name Report",
    title: "Why does naming your child feel heavier than every other decision?",
    description:
      "A personal namkaran report: the child's janma nakshatra and pada, the auspicious starting syllables, and 30+ curated names with meanings and numerology fit.",
    lead: "In namkaran, the first syllable is chosen from the child's janma nakshatra and pada, so the name carries the Moon's blessing from birth. A trending name from a list skips this entirely. This report gives the exact syllables the nakshatra opens, then names that fit them.",
    checkpoints: [
      "The child's janma nakshatra and pada from birth details",
      "Auspicious starting syllables (namkaran akshar) the nakshatra opens",
      "30+ curated names for those syllables, each with meaning",
      "Numerology fit of each name with the child's birth number",
    ],
    fit: "Best when the naming ceremony is near and you want the nakshatra-correct syllables before choosing, or to check a name you already have in mind.",
    inputSurface: "kundali",
    offerHeadline: "Get your Baby Name Report",
    offerSubhead:
      "Enter the child's birth details first, then get the janma nakshatra, the auspicious starting syllables, and 30+ fitted names with meanings.",
    collectInputsLabel: "Enter birth details for Baby Name Report",
    customerProblem: "baby_name",
    faq: [
      {
        q: "How is this different from a free baby-name list?",
        a: "A free list is not tied to your child's chart. This report first computes the janma nakshatra and pada, gives the exact auspicious syllables those open, and only then suggests names.",
      },
      {
        q: "What details do I need for the child?",
        a: "Date, time, and place of birth. The Moon's nakshatra and pada at birth decide the starting syllables, so the birth time should be as accurate as possible.",
      },
    ],
  },
  "lal-kitab-report": {
    slug: "lal-kitab-report",
    reportSlug: "lal-kitab",
    intent: "remedy_dosha",
    eyebrow: "Lal Kitab Remedy Report",
    title: "Why does the same money or family trouble keep circling back?",
    description:
      "A personal Lal Kitab report: your Lal Kitab chart, the active ancestral debts (rin), blind and sleeping planets, and the simple low-cost totke matched to your chart.",
    lead: "Lal Kitab reads your chart as a ledger of rin — ancestral debts — and blind or sleeping planets giving no result. A totka from folklore only helps if it matches the debt being collected. This report names which rin is active in your chart and the totke that clear it.",
    checkpoints: [
      "Your Lal Kitab chart in its own house-and-debt system",
      "Active ancestral debts (rin) and the life area each one presses on",
      "Blind and sleeping planets that need waking correctly",
      "Simple, low-cost totke matched to your chart, in prescribed order",
    ],
    fit: "Best when a money or family pattern keeps repeating and you want low-cost, home-doable remedies matched to the actual debt, not generic totke.",
    inputSurface: "kundali",
    offerHeadline: "Get your Lal Kitab Remedy Report",
    offerSubhead:
      "Generate your kundali first, then get your Lal Kitab chart, the active rin, the blind planets, and the simple totke matched to your chart.",
    collectInputsLabel: "Enter birth details for Lal Kitab Report",
    customerProblem: "lal_kitab",
    faq: [
      {
        q: "How is Lal Kitab different from my normal kundali reading?",
        a: "Lal Kitab uses its own house system and reads the chart as ancestral debts (rin) and blind or sleeping planets, pointing at low-cost totke rather than gemstones or long pujas.",
      },
      {
        q: "Are the totke complicated or expensive?",
        a: "No. Lal Kitab totke are deliberately simple and low-cost. The report gives only the ones matched to your active rin, in the right order.",
      },
    ],
  },
  "gochar-report": {
    slug: "gochar-report",
    reportSlug: "gochar-transit",
    intent: "chart_report",
    eyebrow: "Gochar Transit Forecast",
    title: "Why does one year lift everything and the next feel uphill?",
    description:
      "A personal gochar report: Shani and Guru transits over your chart, the key ingress dates, the houses each transit activates, and a 12-month timing map.",
    lead: "The climate around you is set by gochar — the live transits of Shani and Guru over your chart. Effort feels blocked or supported depending on where they are moving through your houses. This report maps those transits and the months the pressure and the openings actually arrive.",
    checkpoints: [
      "Shani and Guru gochar over your chart from your Moon sign",
      "Key ingress dates when each planet changes rashi",
      "The houses each transit activates and the life area it touches",
      "A 12-month timing map: the windows to act and the windows to hold",
    ],
    fit: "Best before a career move, purchase, or major commitment when you want to time it to Saturn and Jupiter's next ingress instead of fighting the transit.",
    inputSurface: "kundali",
    offerHeadline: "Get your Gochar Transit Forecast",
    offerSubhead:
      "Generate your kundali first, then get Shani and Guru transits over your houses, the ingress dates, and the 12-month act-and-hold timing map.",
    collectInputsLabel: "Enter birth details for Gochar Forecast",
    customerProblem: "gochar",
    faq: [
      {
        q: "How is this different from a monthly horoscope?",
        a: "A monthly horoscope is written for a whole rashi. This forecast reads Saturn and Jupiter moving through your own houses from your Moon sign, with exact ingress dates and the 12-month windows that matter for your chart.",
      },
      {
        q: "Is this the same as a Sade Sati report?",
        a: "Sade Sati is Shani's phase over your Moon sign specifically. The gochar forecast is broader — both Shani and Guru transits across all your houses. If Sade Sati is your main concern, the Sade Sati report goes deeper on that one phase.",
      },
    ],
  },
};

const HINDI_REPORT_INTENT_SLUGS = new Set<ReportIntentSlug>([
  "career-report",
  "marriage-report",
  "dosha-report",
  "dosha-remedy-plan",
]);
const REPORT_INTENT_SLUG_SET = new Set<string>(REPORT_INTENT_SLUGS);

/** True only for a report conversion landing, including the Hindi twins that
 * actually exist. Query/hash suffixes and trailing slashes are tolerated so
 * fixed navigation suppression cannot drift from Next's pathname variants. */
export function isReportIntentLandingPath(pathname: string): boolean {
  const path = pathname.split(/[?#]/, 1)[0].replace(/\/+$/, "") || "/";
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 1) return isReportIntentSlug(segments[0]);
  return (
    segments.length === 2 &&
    segments[0] === "hi" &&
    isReportIntentSlug(segments[1]) &&
    HINDI_REPORT_INTENT_SLUGS.has(segments[1])
  );
}

function isReportIntentSlug(value: string): value is ReportIntentSlug {
  return REPORT_INTENT_SLUG_SET.has(value);
}
